import { useCallback, useMemo, useState } from "react";

import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

/** How ink is produced in canvas capture mode: drawn by hand or typed. */
export type SignatureCanvasMode = "draw" | "type";

export const SIGNATURE_CANVAS_MODES: readonly SignatureCanvasMode[] = [
  "draw",
  "type"
];

export function isSignatureCanvasMode(
  value: unknown
): value is SignatureCanvasMode {
  return SIGNATURE_CANVAS_MODES.includes(value as SignatureCanvasMode);
}

/** Bump the version suffix to reset everyone's remembered choice. */
export const SIGNATURE_CANVAS_MODE_STORAGE_KEY =
  "suno_signature_canvas_mode_v1";

export const DEFAULT_SIGNATURE_CANVAS_MODE: SignatureCanvasMode = "draw";

interface StoredSignatureCanvasMode {
  mode: SignatureCanvasMode;
}

function isStoredSignatureCanvasMode(
  data: unknown
): data is StoredSignatureCanvasMode {
  return isSignatureCanvasMode((data as StoredSignatureCanvasMode)?.mode);
}

/**
 * Remembers the Draw/Type choice for THIS browser/workstation.
 *
 * Deliberately a separate key from `useSignatureMethod`: the capture METHOD
 * (canvas vs a Topaz pad) says which device collects the ink, while this mode
 * says how the canvas collects it. Collapsing them into one union would let a
 * stored "type" un-select a workstation's Topaz pad. Callers must read this
 * through the feature gate rather than trusting the stored value, so turning
 * the flag off can never strand a workstation on a mode it cannot render.
 */
export function useSignatureCanvasMode(): {
  canvasMode: SignatureCanvasMode;
  setCanvasMode: (mode: SignatureCanvasMode) => void;
} {
  const storage = useMemo(
    () => setupLocalStorageAPI(SIGNATURE_CANVAS_MODE_STORAGE_KEY),
    []
  );

  const [canvasMode, setCanvasModeState] = useState<SignatureCanvasMode>(() => {
    const stored = storage.getData(null, isStoredSignatureCanvasMode);
    return isStoredSignatureCanvasMode(stored)
      ? stored.mode
      : DEFAULT_SIGNATURE_CANVAS_MODE;
  });

  const setCanvasMode = useCallback(
    (next: SignatureCanvasMode) => {
      setCanvasModeState(next);
      storage.updateData({ mode: next });
    },
    [storage]
  );

  return { canvasMode, setCanvasMode };
}
